import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { selectProjectLoading, selectProjectInitialized } from "./ProjectSlice_Store";
import { uploadDraftToProject } from "./services/draftUploadService";
import { useAppSelector } from "./hooks/reduxHooks";

export default function DraftEditor_Component(): JSX.Element {
  const [content, setContent] = useState<string>("");
  const [filename, setFilename] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [searchParams] = useSearchParams();

  // Get current project ID from Redux (will be used as default)
  const { currentProjectId } = useAppSelector(state => state.projectSlice);
  const projectLoading = useAppSelector(selectProjectLoading);
  const projectInitialized = useAppSelector(selectProjectInitialized);

  // Get current user info from Redux auth state
  const reduxAuth = useAppSelector(state => state.userAuthAndAdminSlice);

  // Manual project ID field (defaults to URL param, then Redux project ID)
  const [projectId, setProjectId] = useState<string>("");

  // Get current user ID from Redux state (supports both MSAL and custom auth)
  const getCurrentUserId = (): string => {
    if (reduxAuth?.isAuthenticated && reduxAuth?.userId) {
      return reduxAuth.userId as string;
    }
    if (reduxAuth?.isAuthenticated && reduxAuth?.userEmail) {
      return reduxAuth.userEmail as string;
    }
    return "test.user"; // Fallback
  };

  // Initialize projectId from URL or Redux on mount
  React.useEffect(() => {
    // Priority: 1) URL param, 2) Redux state
    const urlProjectId = searchParams.get('projectId');
    if (urlProjectId && !projectId) {
      setProjectId(urlProjectId);
    } else if (currentProjectId && !projectId) {
      setProjectId(currentProjectId);
    }
  }, [currentProjectId, projectId, searchParams]);

  const handleUpload = async () => {
    if (!content || content.trim() === '') {
      setError("Please enter some content");
      return;
    }

    if (!filename || filename.trim() === '') {
      setError("Please enter a filename");
      return;
    }

    if (!projectId || projectId.trim() === '') {
      setError("Please enter a project ID");
      return;
    }

    setUploading(true);
    setError("");
    setUploadStatus("Uploading draft...");

    try {
      const { filePath } = await uploadDraftToProject({
        content,
        filename,
        projectId: projectId.trim(),
        username: getCurrentUserId(),
        onStatusChange: (status: string) => {
          if (status === 'fetching_sas') {
            setUploadStatus("Getting upload URL...");
          }
          if (status === 'uploading') {
            setUploadStatus("Uploading draft...");
          }
          if (status === 'completed') {
            setUploadStatus("Upload complete!");
          }
        }
      });

      setUploadStatus(`Draft uploaded successfully to ${filePath}! The draft will be added to your project context shortly.`);

      // Clear form after successful upload
      setContent("");
      setFilename("");

    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-6">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Simulate adding a generated doc to the project
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create markdown drafts that will be automatically added to your project context
          </p>
        </div>

        {!projectInitialized && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Waiting for project to initialize...
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Project ID Input */}
          <div>
            <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-2">
              Project ID (paste from console logs)
            </label>
            <input
              type="text"
              id="projectId"
              value={projectId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setProjectId(e.target.value);
                setError("");
                setUploadStatus("");
              }}
              placeholder="e.g., e7be688a-c83b-4e77-897e-ae0c7249acb0"
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Copy the project ID from your chat window's URL (e.g., ?projectId=xxx) or console logs
            </p>
          </div>

          {/* Filename Input */}
          <div>
            <label htmlFor="filename" className="block text-sm font-medium text-gray-700 mb-2">
              Filename (will be saved as .md)
            </label>
            <input
              type="text"
              id="filename"
              value={filename}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFilename(e.target.value);
                setError("");
                setUploadStatus("");
              }}
              placeholder="my-draft-document"
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Content Textarea */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Markdown Content
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setContent(e.target.value);
                setError("");
                setUploadStatus("");
              }}
              placeholder="# My Draft Document

Write your markdown content here...

You can use:
- Lists
- **Bold text**
- *Italic text*
- [Links](https://example.com)
- And more!"
              disabled={uploading}
              rows={20}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed resize-y"
            />
          </div>

          {/* Character Count */}
          <div className="text-sm text-gray-500 text-right">
            {content.length} characters
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading || !content.trim() || !filename.trim() || !projectId.trim()}
            className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cvc-blue hover:bg-cvc-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cvc-blue disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading Draft...
              </span>
            ) : (
              "Upload Draft to Project"
            )}
          </button>

          {/* Status Messages */}
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {uploadStatus && !error && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{uploadStatus}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
